/**
 * THE CLIENT'S CONFIG STATE, AND THE GATE THAT RUNS BEFORE A MATCH.
 *
 * ── THE ADR-0004 CONTRACT, ON THIS SIDE ─────────────────────────────────────
 * The bundled card table is a cache. Before a match starts, the client asks the
 * server which version is authoritative; if it differs from what the client
 * holds, the client fetches the server's table, VALIDATES it, and adopts it —
 * and only then joins. It never joins on a version it knows to be wrong, and it
 * never adopts a table it could not verify.
 *
 * `checkVersions()` in `@rga/proto` refuses a mismatched client at the join gate
 * with `REASON_CARD_TABLE_STALE`, so the worst case of skipping this gate is a
 * refused join rather than a desynced match. This exists to turn that refusal
 * into a refresh the player never sees.
 *
 * ── WHY THE ROSTER IS NOT IMPORTED HERE ─────────────────────────────────────
 * The store deliberately holds `cards: null` while the bundled table is current
 * rather than importing `DEFAULT_CARDS` from `@rga/sim`. Importing it would pull
 * the sim's 52 card JSONs into the SHELL bundle, which is measured against a
 * 150 KB gz budget the shell is already at 108 of (ADR-0005). The match layer
 * already has the sim; it uses `cards ?? undefined`, and `createMatch` falls
 * back to exactly the same bundled array.
 *
 * ── PERSISTENCE ─────────────────────────────────────────────────────────────
 * The adopted document is cached in `localStorage` so a returning player is
 * already current on the first frame instead of after a round trip. The cache
 * is re-validated on read with the same function the network path uses — a
 * corrupted or hand-edited entry is discarded, not trusted because it is local.
 */
import { createStore, type Store } from '@/core/state/store';
import type { Card } from '@rga/sim';

import { defaultConfigApi, type ConfigApi } from './config-api';
import { validateEnvelope } from './config-validate';
import { readFlag } from './flags';
import {
  BUNDLED_CARD_TABLE_VERSION,
  CONFIG_CACHE_KEY,
  type ConfigState,
  type FreshnessResult,
  type PublicFlag,
  type RemoteConfigEnvelope,
} from './config.types';

const INITIAL: ConfigState = Object.freeze({
  revision: 0,
  cardTableVersion: BUNDLED_CARD_TABLE_VERSION,
  checksum: '',
  cardsChecksum: '',
  cards: null,
  flags: Object.freeze([]) as readonly PublicFlag[],
  answers: null,
  loaded: false,
  publishedAt: '',
});

/** The one config store. Subscribe to redraw a screen when a flag flips. */
export const configStore: Store<ConfigState> = createStore<ConfigState>(INITIAL);

/** The version this client will claim on a join. Never `0`. */
export function activeCardTableVersion(): number {
  return configStore.get().cardTableVersion;
}

/**
 * The roster a match should be built with, or `undefined` for "use the bundled
 * one". Hand it straight to `createMatch({ cards })`.
 */
export function activeCards(): readonly Card[] | undefined {
  return configStore.get().cards ?? undefined;
}

/** Is this feature on for this viewer? A hint for drawing — never a decision. */
export function isFeatureEnabled(key: string, subjectId = ''): boolean {
  const state = configStore.get();
  return readFlag(key, state.flags, state.answers, subjectId);
}

/**
 * Adopt whatever is in `localStorage`, if it still validates.
 *
 * Called once at boot, before anything renders. Cheap, synchronous, and it is
 * what makes a returning player's first frame already correct.
 */
export function hydrateConfigFromCache(): boolean {
  const raw = readStorage(CONFIG_CACHE_KEY);
  if (raw === null) return false;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearStorage(CONFIG_CACHE_KEY);
    return false;
  }
  // `-1` skips the monotonicity check: at boot there is nothing to be newer
  // than, and a cache entry is by definition the last thing adopted.
  const outcome = validateEnvelope(parsed, -1);
  if (!outcome.ok) {
    clearStorage(CONFIG_CACHE_KEY);
    return false;
  }
  adopt(parsed as RemoteConfigEnvelope, false);
  return true;
}

/**
 * THE PRE-MATCH GATE. Await this before joining a queue or a lobby.
 *
 * One small request in the common case (`/config/version`), and a full document
 * fetch only when the server says the client is behind.
 */
export async function ensureCardTableFresh(api: ConfigApi = defaultConfigApi): Promise<FreshnessResult> {
  const before = configStore.get();
  let version;
  try {
    version = await api.version();
  } catch {
    // A failed freshness check is NOT a reason to block play. The join gate is
    // still there and still refuses a genuine mismatch with a reason the queue
    // screen can render; blocking here would turn one flaky request into "you
    // cannot start a match".
    return { status: 'offline', cardTableVersion: before.cardTableVersion, revision: before.revision, reason: null };
  }

  if (version.revision === before.revision && version.checksum === before.checksum && before.loaded) {
    return { status: 'fresh', cardTableVersion: before.cardTableVersion, revision: before.revision, reason: null };
  }
  // Anything moved — a new roster, or only the flags — so fetch and validate
  // the whole document. It is one request either way, and taking the flags with
  // it means the pre-match check is also what keeps an event tab honest.
  // The caller reads `status`: `refreshed` is safe to join on, `rejected` is
  // not, and `offline` is the caller's judgement (the join gate still refuses a
  // genuine mismatch, with a reason the queue screen can render).
  return refreshDocument(api, before.revision);
}

/**
 * Fetch, validate and adopt the live document. Used by the gate and callable
 * directly (a "flags may have changed" refresh after returning to the menu).
 */
export async function refreshDocument(
  api: ConfigApi = defaultConfigApi,
  since = configStore.get().revision,
): Promise<FreshnessResult> {
  const before = configStore.get();
  let envelope: RemoteConfigEnvelope;
  try {
    envelope = await api.document();
  } catch {
    return { status: 'offline', cardTableVersion: before.cardTableVersion, revision: before.revision, reason: null };
  }
  const outcome = validateEnvelope(envelope, before.loaded ? since : -1);
  if (!outcome.ok) {
    // KEEP what we had. A document that does not validate is not evidence that
    // the current one is wrong, and swapping to a table nobody verified is the
    // one outcome ADR-0004 exists to prevent.
    return {
      status: 'rejected',
      cardTableVersion: before.cardTableVersion,
      revision: before.revision,
      reason: outcome.reason,
    };
  }
  adopt(envelope, true);
  return { status: 'refreshed', cardTableVersion: envelope.cardTableVersion, revision: envelope.revision, reason: null };
}

/**
 * Fetch the server's own flag answers for the signed-in account.
 *
 * Failure is silent and non-fatal: the store keeps evaluating the public
 * document locally, which is right for every flag that is not individually
 * overridden. A signed-out caller gets a 401 and stays on local evaluation,
 * which is exactly the intended behaviour.
 */
export async function refreshFlagAnswers(api: ConfigApi = defaultConfigApi): Promise<boolean> {
  try {
    const view = await api.answers();
    if (view === null || typeof view !== 'object' || typeof view.flags !== 'object' || view.flags === null) {
      return false;
    }
    configStore.set((prev) => ({ ...prev, answers: view.flags }));
    return true;
  } catch {
    return false;
  }
}

/**
 * The archived roster a replay pins (ADR-0004).
 *
 * A replay stores `cardTableVersion` and nothing else about the table, so
 * reproducing one recorded before a balance publish means asking the server for
 * the table that number meant. Returns `undefined` for the version already
 * loaded (no request) and `null` when the server has no such table.
 */
export async function cardsForReplay(
  version: number,
  api: ConfigApi = defaultConfigApi,
): Promise<readonly Card[] | null | undefined> {
  const state = configStore.get();
  if (version === state.cardTableVersion) return state.cards ?? undefined;
  try {
    const table = await api.cardTable(version);
    return Array.isArray(table.cards) && table.cards.length > 0 ? table.cards : null;
  } catch {
    return null;
  }
}

/** Reset to the bundled defaults and forget the cache. For tests and sign-out. */
export function resetConfigState(): void {
  configStore.set(INITIAL);
  clearStorage(CONFIG_CACHE_KEY);
}

/* ══════════════════════════════════════════════════════════════ internals ══ */

function adopt(envelope: RemoteConfigEnvelope, persist: boolean): void {
  configStore.set({
    revision: envelope.revision,
    cardTableVersion: envelope.cardTableVersion,
    checksum: envelope.checksum,
    cardsChecksum: envelope.cardsChecksum,
    // The BUNDLED version keeps `cards: null` so the match layer uses the array
    // the sim already holds rather than a structurally identical copy of it.
    cards: envelope.cardTableVersion === BUNDLED_CARD_TABLE_VERSION ? null : envelope.payload.cards,
    flags: envelope.payload.flags,
    // Server answers are per-account and per-revision; a new document
    // invalidates them, and re-evaluating locally is correct until they arrive.
    answers: null,
    loaded: true,
    publishedAt: envelope.publishedAt,
  });
  if (persist) writeStorage(CONFIG_CACHE_KEY, JSON.stringify(envelope));
}

/**
 * `localStorage` is wrapped because it THROWS rather than returning null in
 * private-mode Safari, in an iframe with third-party storage blocked, and when
 * the quota is full. A config layer that can crash the boot over a storage
 * preference is not one worth having.
 */
function readStorage(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    /* a cache that cannot be written is a cache miss next time; nothing else */
  }
}

function clearStorage(key: string): void {
  try {
    globalThis.localStorage?.removeItem(key);
  } catch {
    /* see above */
  }
}
